import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { debounce } from '@ember/runloop';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';

import type { BlockThemeConfig, Service } from 'webapp-types';
import { DefaultColor } from 'webapp/const/board-color-config';

import didInsert from 'webapp/modifiers/did-insert';
import willDestroy from 'webapp/modifiers/will-destroy';
import BlockWrapperComponent from 'webapp/components/block/wrapper';

import type BoardState from 'webapp/models-custom/board-state';

export interface Args {
	boardState: BoardState;
	colorConfig?: BlockThemeConfig.Parsed;
	showBg?: boolean;
	extraClasses?: string;
	onClick?: () => void;
	skipBlockFetchLoader?: boolean;
	isFromBlockPreview?: boolean;
	// the data status popup then only describes the status
	skipActions?: boolean;
}

interface BlockSingleComponentInterface<T> {
	Args: T;
	Blocks: { default: [] }; // this is needed for yield
}

export default class BlockSingleComponent extends Component<BlockSingleComponentInterface<Args>> {
	resizeObserver: ResizeObserver | null = null;

	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('navigation') declare navigationService: Service.Navigation;

	get block() {
		return this.args.boardState.selectedBlock!;
	}

	get boardColor() {
		return (
			this.args.colorConfig?.bgColor ||
			(this.navigationService.theme.isDark ? DefaultColor : '#ffffff')
		);
	}

	get skipBlockFetchLoader() {
		return !!this.args.skipBlockFetchLoader;
	}

	@cached
	get colorConfig() {
		return this.args.colorConfig || this.mmBoardsService.getColorConfig(this.boardColor, 1);
	}

	get colorGradientStyle() {
		return htmlSafe(
			this.args.showBg ? this.mmBoardsService.getColorGradient(this.boardColor) : '',
		);
	}

	@action
	updatePositioning(node: HTMLElement) {
		$(node).find('.preview-block').trigger('resize.block');
	}

	@action
	setupView(node: HTMLElement) {
		const resizeObserver = new ResizeObserver(() => {
			debounce(this, this.updatePositioning, node, 400);
		});
		resizeObserver.observe(node);
		this.resizeObserver = resizeObserver;
	}

	@action
	teardownView(node: HTMLElement): void {
		this.resizeObserver?.unobserve(node);
	}

	@action
	handleClick() {
		const onClick = this.args.onClick;
		if (onClick) {
			onClick();
		}
	}

	@action
	doNothing() {
		// what the name says
	}
	<template>
		<div
			class="view-single-block bgTheme-{{this.colorConfig.bgTheme}}
				colorTheme-{{this.colorConfig.themeId}}
				{{@extraClasses}}"
			role="button"
			{{on "click" this.handleClick}}
			{{didInsert this.setupView}}
			{{willDestroy this.teardownView}}
		>
			<div class="view-single-block-content" style={{this.colorGradientStyle}}>
				<BlockWrapperComponent
					@model={{this.block}}
					@boardState={{@boardState}}
					@colorConfig={{this.colorConfig}}
					@isWallView={{true}}
					@currentZoom={{100}}
					@skipBlockFetchLoader={{this.skipBlockFetchLoader}}
					@skipMasterFilter={{true}}
					@skipActions={{@skipActions}}
					@onBlockDelete={{this.doNothing}}
				/>
			</div>

			{{yield}}
		</div>
	</template>
}
